import type { TenantScenarioBucketItem } from "@visioni/a3s-traffic-flow/client-messages";

export type ClassName = "car" | "human" | "bike" | "bus" | "truck" | "otherVehicle";

export type Period = "1d" | "3d" | "7d" | "30d";

export interface SummariseBucketsRow {
  class: ClassName;
  total: number;
  avgPerDay: number;
  peakHour: string;
}

export interface SummariseBucketsResult {
  rows: SummariseBucketsRow[];
  totalDays: number;
}

const CLASSES: readonly ClassName[] = ["car", "human", "bike", "bus", "truck", "otherVehicle"];

const PERIOD_DAYS: Record<Period, number> = {
  "1d": 1,
  "3d": 3,
  "7d": 7,
  "30d": 30,
};

const NO_DATA_HOUR = "--:--";

// `intervalStartLocal` is shaped `YYYY-MM-DDTHH:MM:SS±HHMM` (or `±HH:MM`).
// We only need the local hour-of-day so a fixed-offset substring is enough —
// no Date parsing, which would coerce into the runtime's timezone.
function localHour(intervalStartLocal: string): number | null {
  if (intervalStartLocal.length < 13 || intervalStartLocal[10] !== "T") return null;
  const hour = Number.parseInt(intervalStartLocal.slice(11, 13), 10);
  if (Number.isNaN(hour) || hour < 0 || hour > 23) return null;
  return hour;
}

function formatHour(hour: number): string {
  return `${hour.toString().padStart(2, "0")}:00`;
}

export function summariseBuckets(buckets: TenantScenarioBucketItem[], period: Period): SummariseBucketsResult {
  const totalDays = PERIOD_DAYS[period];
  if (buckets.length === 0) {
    return {
      rows: CLASSES.map((cls) => ({ class: cls, total: 0, avgPerDay: 0, peakHour: NO_DATA_HOUR })),
      totalDays,
    };
  }

  const totals: Record<ClassName, number> = { car: 0, human: 0, bike: 0, bus: 0, truck: 0, otherVehicle: 0 };
  const hourlyByClass: Record<ClassName, Map<number, number>> = {
    car: new Map(),
    human: new Map(),
    bike: new Map(),
    bus: new Map(),
    truck: new Map(),
    otherVehicle: new Map(),
  };

  for (const bucket of buckets) {
    const hour = localHour(bucket.intervalStartLocal);
    for (const cls of CLASSES) {
      const count = bucket[cls];
      totals[cls] += count;
      if (hour !== null && count > 0) {
        const map = hourlyByClass[cls];
        map.set(hour, (map.get(hour) ?? 0) + count);
      }
    }
  }

  const rows = CLASSES.map<SummariseBucketsRow>((cls) => {
    const total = totals[cls];
    const hourly = hourlyByClass[cls];
    let peakHour = NO_DATA_HOUR;
    if (hourly.size > 0) {
      let bestHour = 24;
      let bestCount = -1;
      for (const [hour, count] of hourly) {
        // Tie-break: earliest hour wins, matching the plan's spec.
        if (count > bestCount || (count === bestCount && hour < bestHour)) {
          bestCount = count;
          bestHour = hour;
        }
      }
      if (bestHour < 24) peakHour = formatHour(bestHour);
    }
    return {
      class: cls,
      total,
      avgPerDay: Math.round(total / totalDays),
      peakHour,
    };
  });

  return { rows, totalDays };
}
